import { getSupabaseClient } from '@/integrations/supabase/client';
import { Sale } from '@/types';
import {
  TAB_ITEM_MODIFIERS_EMBED,
  mapTabItemModifierRows,
} from '@/services/products/tabItemModifiersService';
import type { SaleSellerRef } from './resolveSaleSeller';

const SALES_PAGE_SIZE = 1000;

const SALES_SELECT = `
  *,
  tabs (
    tab_items (
      id,
      product_id,
      product_name,
      product_category,
      quantity,
      unit_price,
      discount,
      weight_kg,
      tab_item_promotion_components (
        id,
        tab_item_id,
        component_product_id,
        quantity,
        product_name,
        created_at
      ),
      ${TAB_ITEM_MODIFIERS_EMBED},
      products (
        category_id,
        product_categories (
          cabys_code,
          cabys_tax,
          activity_code
        )
      )
    )
  )
`;

export const salesService = {
  async getSales(organizationId: string, from: Date, to: Date) {
    const supabase = getSupabaseClient();

    const data: any[] = [];
    let pageFrom = 0;

    while (true) {
      const { data: page, error } = await supabase
        .from('sales')
        .select(SALES_SELECT)
        .eq('organization_id', organizationId)
        .gte('sale_date', from.toISOString())
        .lte('sale_date', to.toISOString())
        .order('sale_date', { ascending: false })
        .range(pageFrom, pageFrom + SALES_PAGE_SIZE - 1);

      if (error) throw error;
      if (!page?.length) break;

      data.push(...page);
      if (page.length < SALES_PAGE_SIZE) break;

      pageFrom += SALES_PAGE_SIZE;
    }

    if (data.length === 0) return [];
    
    // Get unique user IDs from sales
    const userIds = [...new Set(data.map(sale => sale.sold_by).filter(Boolean))];
    
    let profilesData: { id: string; full_name: string }[] = [];
    if (userIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      
      if (!profilesError && profiles) {
        profilesData = profiles;
      }
    }
    
    const profileMap = new Map<string, { id: string; full_name: string }>();
    profilesData.forEach(profile => {
      profileMap.set(profile.id, profile);
    });

    const simpleUserIds = [
      ...new Set(data.map((sale) => sale.sold_by_simple_user_id).filter(Boolean)),
    ];
    let simpleUsersData: { id: string; username: string }[] = [];
    if (simpleUserIds.length > 0) {
      const { data: simpleUsers, error: simpleUsersError } = await supabase
        .from('simple_users')
        .select('id, username')
        .in('id', simpleUserIds);
      if (!simpleUsersError && simpleUsers) {
        simpleUsersData = simpleUsers;
      }
    }
    const simpleUserMap = new Map(
      simpleUsersData.map((su) => [su.id, su]),
    );
    
    // Get all payment methods to map names correctly
    const { data: allPaymentMethods, error: paymentMethodsError } = await supabase
      .from('payment_methods')
      .select('id, name, icon, is_active, default_payment_method_id')
      .eq('organization_id', organizationId);
    
    if (paymentMethodsError) {
      console.error('Error fetching payment methods:', paymentMethodsError);
    }
    
    const paymentMethodMap = new Map();
    if (allPaymentMethods) {
      allPaymentMethods.forEach(pm => {
        paymentMethodMap.set(pm.id, pm);
      });
    }
    
    return data.map(sale => {
      // Extract totalDollarsReceived from JSONB if it exists
      let totalDollarsReceived: number | undefined;
      let paymentMethodsData = sale.payment_methods;
      
      if (sale.payment_methods && Array.isArray(sale.payment_methods)) {
        // Check if the last item is totalDollarsReceived
        const lastItem = sale.payment_methods[sale.payment_methods.length - 1];
        if (lastItem && lastItem.totalDollarsReceived !== undefined) {
          totalDollarsReceived = Number(lastItem.totalDollarsReceived);
          // Remove the totalDollarsReceived item from the array
          paymentMethodsData = sale.payment_methods.slice(0, -1);
        }
      }
      
      const paymentMethods = paymentMethodsData && Array.isArray(paymentMethodsData) 
        ? paymentMethodsData.map((pm: any, index: number) => {
            const paymentMethodInfo = paymentMethodMap.get(pm.methodId);
            return {
              id: `${sale.id}-${pm.methodId}-${index}`, 
              paymentMethod: {
                id: pm.methodId,
                name: paymentMethodInfo?.name || 'Unknown',
                icon: paymentMethodInfo?.icon || '',
                isActive: paymentMethodInfo?.is_active || false,
                createdAt: new Date(),
                organizationId,
                defaultPaymentMethodId: paymentMethodInfo?.default_payment_method_id
              },
              amount: Number(pm.amount)
            };
          })
        : [];
      
      return {
        id: sale.id,
        tabId: sale.tab_id,
        customerName: sale.customer_name,
        items: sale.tabs?.tab_items?.map((item: any) => {
          const rawComps = item.tab_item_promotion_components ?? [];
          const promotionComponents = rawComps
            .filter((c: any) => c != null)
            .sort(
              (a: any, b: any) =>
                String(a.product_name ?? '').localeCompare(String(b.product_name ?? '')) ||
                String(a.id ?? '').localeCompare(String(b.id ?? ''))
            )
            .map((c: any) => ({
              id: c.id,
              tabItemId: c.tab_item_id,
              componentProductId: c.component_product_id,
              quantity: Number(c.quantity ?? 0),
              productName: c.product_name,
            }));

          return {
            id: item.id,
            productId: item.product_id,
            product: {
              id: item.product_id,
              name: item.product_name,
              category: item.product_category,
              categoryId: item.products?.category_id || '',
              cost: 0,
              price: Number(item.unit_price),
              stock: 0,
              organizationId,
              productCategory: item.products?.product_categories
                ? {
                    cabysCode: item.products.product_categories.cabys_code,
                    cabysTax: item.products.product_categories.cabys_tax,
                    activityCode: item.products.product_categories.activity_code,
                  }
                : null,
            },
            quantity: Number(item.quantity),
            unitPrice: Number(item.unit_price),
            discount: Number(item.discount || 0),
            weightKg: item.weight_kg != null ? Number(item.weight_kg) : undefined,
            promotionComponents: promotionComponents.length ? promotionComponents : undefined,
            modifiers: mapTabItemModifierRows(item.tab_item_modifiers),
          };
        }) || [],
        subtotal: Number(sale.subtotal),
        discount: Number(sale.discount || 0),
        serviceCharge: sale.service_charge ? Number(sale.service_charge) : undefined,
        serviceChargeRate: sale.service_charge_rate ? Number(sale.service_charge_rate) : undefined,
        tip: sale.tip != null ? Number(sale.tip) : undefined,
        iva: sale.iva ? Number(sale.iva) : undefined,
        ivaRate: sale.iva_rate ? Number(sale.iva_rate) : undefined,
        total: Number(sale.total),
        paymentMethods: paymentMethods,
        saleDate: new Date(sale.sale_date),
        soldBy: sale.sold_by ?? undefined,
        soldBySimpleUserId: sale.sold_by_simple_user_id ?? undefined,
        soldByName: sale.sold_by_name || undefined,
        soldBySimpleUser:
          !sale.sold_by_name?.trim() && sale.sold_by_simple_user_id
            ? {
                id: sale.sold_by_simple_user_id,
                username:
                  simpleUserMap.get(sale.sold_by_simple_user_id)?.username ?? 'Unknown',
              }
            : null,
        soldByProfile:
          !sale.sold_by_name?.trim() && sale.sold_by
            ? {
                id: sale.sold_by,
                full_name: profileMap.get(sale.sold_by)?.full_name ?? 'Unknown',
              }
            : null,
        organizationId: sale.organization_id,
        sinpePhotoUrl: sale.sinpe_photo_url || undefined,
        cardLastFourDigits: sale.card_last_four_digits || undefined,
        totalDollarsReceived: totalDollarsReceived,
        invoiceId: sale.invoice_id || undefined
      };
    }) as Sale[];
  },

  /**
   * Records a sale with a client-generated id so the operation is replayable
   * offline. The upsert conflicts on `tab_id` (the `unique_tab_sale` constraint:
   * one sale per tab), so a replayed or double-fired close is a no-op instead of
   * a 23505 error. The sale date comes from the caller and is preserved when a
   * queued mutation replays later. Electronic-ticket creation is handled by the
   * offline outbox (see offlineMutations), not here.
   */
  async createSale({
    saleId,
    tabId,
    customerName,
    subtotal,
    discount,
    serviceCharge,
    serviceChargeRate,
    tip,
    iva,
    ivaRate,
    total,
    paymentMethodsJson,
    sinpePhotoUrl,
    cardLastFourDigits,
    seller,
    organizationId,
    saleDateIso,
  }: {
    saleId: string;
    tabId: string;
    customerName: string;
    subtotal: number;
    /** Tab discount percentage; 100% is stored as NULL (legacy behavior). */
    discount: number;
    serviceCharge?: number;
    serviceChargeRate?: number;
    tip?: number;
    iva?: number;
    ivaRate?: number;
    total: number;
    paymentMethodsJson: Array<
      { methodId: string; amount: number } | { totalDollarsReceived: number }
    >;
    sinpePhotoUrl?: string;
    cardLastFourDigits?: string;
    seller: SaleSellerRef;
    organizationId: string;
    saleDateIso: string;
  }) {
    const supabase = getSupabaseClient();

    const row = {
      id: saleId,
      tab_id: tabId,
      customer_name: customerName,
      subtotal: subtotal,
      discount: discount === 100 ? null : discount,
      service_charge: serviceCharge,
      service_charge_rate: serviceChargeRate,
      tip: tip ?? 0,
      iva: iva,
      iva_rate: ivaRate,
      total: total,
      payment_methods: paymentMethodsJson,
      sinpe_photo_url: sinpePhotoUrl || null,
      card_last_four_digits: cardLastFourDigits || null,
      sold_by: seller.type === 'member' ? seller.userId : null,
      sold_by_simple_user_id: seller.type === 'simple_user' ? seller.simpleUserId : null,
      organization_id: organizationId,
      sale_date: saleDateIso,
    };

    const { error } = await supabase
      .from('sales')
      .upsert([row], { onConflict: 'tab_id', ignoreDuplicates: true });

    if (error) throw error;

    // The row is fully determined by the input; no read-back needed.
    return row;
  },

  async updateSalePaymentMethods(saleId: string, paymentMethods: { methodId: string; amount: number }[]) {
    const supabase = getSupabaseClient();

    const paymentMethodsJson = paymentMethods.map(method => ({
      methodId: method.methodId,
      amount: method.amount
    }));
    
    const { data, error } = await supabase
      .from('sales')
      .update({ 
        payment_methods: paymentMethodsJson,
      })
      .eq('id', saleId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateSaleInvoiceId(saleId: string, invoiceId?: string | null) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('sales')
      .update({
        invoice_id: invoiceId ?? null,
      })
      .eq('id', saleId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};